import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindProductStandardValuesQueryResponse,
  FindProductStandardValuesQueryParams,
} from "../types/FindProductStandardValues"

type FindProductStandardValuesClient = typeof client<
  FindProductStandardValuesQueryResponse,
  Error,
  never
>
type FindProductStandardValues = {
  data: FindProductStandardValuesQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: FindProductStandardValuesQueryParams
  headerParams: never
  response: Awaited<ReturnType<FindProductStandardValuesClient>>
  client: {
    parameters: Partial<Parameters<FindProductStandardValuesClient>[0]>
    return: Awaited<ReturnType<FindProductStandardValuesClient>>
  }
}
export const findProductStandardValuesQueryKey = (
  params: FindProductStandardValues["queryParams"]
) =>
  [
    { url: "/v1/product/find-product-standard-values" },
    ...(params ? [params] : []),
  ] as const
export type FindProductStandardValuesQueryKey = ReturnType<
  typeof findProductStandardValuesQueryKey
>
export function findProductStandardValuesQueryOptions<
  TData = FindProductStandardValues["response"],
  TQueryData = FindProductStandardValues["response"],
>(
  params: FindProductStandardValues["queryParams"],
  options: FindProductStandardValues["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindProductStandardValues["response"],
    FindProductStandardValues["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findProductStandardValuesQueryKey(params)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindProductStandardValues["data"],
        FindProductStandardValues["error"]
      >({
        method: "get",
        url: `/v1/product/find-product-standard-values`,
        params,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find product standard values
 * @link /v1/product/find-product-standard-values
 */
export function useFindProductStandardValues<
  TData = FindProductStandardValues["response"],
  TQueryData = FindProductStandardValues["response"],
  TQueryKey extends QueryKey = FindProductStandardValuesQueryKey,
>(
  params: FindProductStandardValues["queryParams"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindProductStandardValues["response"],
        FindProductStandardValues["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindProductStandardValues["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindProductStandardValues["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey =
    queryOptions?.queryKey ?? findProductStandardValuesQueryKey(params)
  const query = useQuery<
    FindProductStandardValues["data"],
    FindProductStandardValues["error"],
    TData,
    any
  >({
    ...findProductStandardValuesQueryOptions<TData, TQueryData>(
      params,
      clientOptions
    ),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindProductStandardValues["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
